import { CheckCircle2, LoaderCircle } from "lucide-react";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getApiErrorMessage, portalApi } from "../api/client";
import type { ApiEnvelope, InvitePreview, TenantInfo } from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { AuthShell } from "../components/AuthShell";
import {
  Field,
  FormError,
  isStrongPassword,
  PasswordInput,
  PasswordRequirements,
  SubmitButton,
} from "../components/FormControls";
import { Button } from "../components/ui";

type PreviewState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; data: InvitePreview };

export function InvitePage({ tenant }: { tenant: TenantInfo }) {
  const { token = "" } = useParams();
  const auth = useAuth();
  const navigate = useNavigate();
  const [preview, setPreview] = React.useState<PreviewState>({
    status: "loading",
  });
  const [password, setPassword] = React.useState("");
  const [confirmation, setConfirmation] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    let active = true;
    portalApi
      .get<ApiEnvelope<InvitePreview>>(
        `/portal/invite/${encodeURIComponent(token)}`,
        { skipAuth: true, skipRefresh: true } as never
      )
      .then(({ data }) => {
        if (active) setPreview({ status: "ready", data: data.data });
      })
      .catch((requestError) => {
        if (active) {
          setPreview({
            status: "error",
            message: getApiErrorMessage(
              requestError,
              "Este convite não é válido ou já foi utilizado."
            ),
          });
        }
      });
    return () => {
      active = false;
    };
  }, [token]);

  async function accept(event: React.FormEvent) {
    event.preventDefault();
    if (preview.status !== "ready") return;
    setError("");

    if (
      preview.data.requiresPassword &&
      (!isStrongPassword(password) || confirmation !== password)
    ) {
      setError("Confira os requisitos e a confirmação da senha.");
      return;
    }

    setLoading(true);
    try {
      const result = await auth.acceptInvite(
        token,
        preview.data.requiresPassword ? password : undefined
      );
      navigate(result.requiresSupplierSelection ? "/select-supplier" : "/", {
        replace: true,
      });
    } catch (requestError) {
      setError(
        getApiErrorMessage(
          requestError,
          "Não foi possível aceitar este convite."
        )
      );
    } finally {
      setLoading(false);
    }
  }

  if (preview.status === "loading") {
    return (
      <AuthShell tenant={tenant} title="Validando convite">
        <div className="flex items-center justify-center gap-2 py-8 text-sm text-slate-600">
          <LoaderCircle className="animate-spin" size={19} />
          Aguarde um instante…
        </div>
      </AuthShell>
    );
  }

  if (preview.status === "error") {
    return (
      <AuthShell
        tenant={tenant}
        title="Convite indisponível"
        description={preview.message}
        footer={
          <Button
            variant="link"
            className="h-auto p-0"
            onClick={() => navigate("/login")}
          >
            Ir para o login
          </Button>
        }
      >
        <FormError message="Solicite um novo convite à empresa responsável pelo portal." />
      </AuthShell>
    );
  }

  return (
    <AuthShell
      tenant={tenant}
      title={`Olá, ${preview.data.name}`}
      description={`${preview.data.tenant} convidou você para representar ${preview.data.supplier}.`}
    >
      <form className="space-y-4" onSubmit={accept}>
        {error && <FormError message={error} />}
        <div className="flex items-start gap-3 rounded-xl border border-teal-200 bg-teal-50 p-4 text-sm text-teal-900">
          <CheckCircle2 className="mt-0.5 shrink-0" size={18} />
          <span>
            O acesso será associado ao e-mail{" "}
            <strong>{preview.data.maskedEmail}</strong>.
          </span>
        </div>
        {preview.data.requiresPassword && (
          <>
            <Field label="Crie sua senha">
              <PasswordInput
                autoComplete="new-password"
                autoFocus
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </Field>
            <PasswordRequirements password={password} />
            <Field
              label="Confirme sua senha"
              error={
                confirmation && confirmation !== password
                  ? "As senhas não são iguais."
                  : undefined
              }
            >
              <PasswordInput
                autoComplete="new-password"
                required
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
              />
            </Field>
          </>
        )}
        <SubmitButton
          loading={loading}
          disabled={
            preview.data.requiresPassword &&
            (!isStrongPassword(password) || confirmation !== password)
          }
        >
          {preview.data.requiresPassword
            ? "Criar senha e acessar"
            : "Aceitar convite e acessar"}
        </SubmitButton>
      </form>
    </AuthShell>
  );
}
